import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { useFetchLaboratoryTestProperties, useFetchLaboratoryTests } from '~/hooks'
import {
  IFetchUrineCultureTestByParams,
  IAntibiotic,
  IFetchAntibioticsParams,
  IGetAntibiotics,
  IGetUrineCultureTests,
  IUrineCultureTest,
  ILaboratoryTestProperty,
  IFetchLaboratoryTestsParams
} from '~/interfaces'
import { message, fetchUrineCultureTests, fetchAntibiotics } from '~/services'
import { urineCultureFilters } from '~/utils'

interface LaboratoryHealthProceduresProviderProps {
  children: React.ReactNode
}

const defaultValue = {
  urineCultureTests: [] as IUrineCultureTest[],
  selectedOrganisms: [] as string[],
  openModalCompareUrineCulture: false,
  // helpers
  isLoading: false,
  error: 'idle'
}

export interface ILaboratoryHealthProceduresContextHelpers {
  isLoading: boolean
  error: string
}

export interface ILaboratoryHealthProceduresContextProps extends ILaboratoryHealthProceduresContextHelpers {
  urineCultureTests: IUrineCultureTest[]
  displayedUrineCulture: IUrineCultureTest[]
  selectedUrineCulture: IUrineCultureTest | null
  selectedUrineCultureProperties?: ILaboratoryTestProperty[]
  isLoadingTestProperties: boolean
  getUrineCultureDetails: (value: IUrineCultureTest) => void
  getHealthProcedures: IGetUrineCultureTests
  filterUrineCulturePerRangeOfDates: (rangeDates: Date[]) => void
  filterUrineCultureByOrganism: (types: string[]) => void
  selectedOrganisms: string[]
  antibiotics: IAntibiotic[] | null
  getAntibiotics: IGetAntibiotics
  setterSelectedOrganisms: (organism: string[]) => void
  rangeOfDatesFilter: Date[]
  openModalCompareUrineCulture: boolean
  setOpenModalCompareUrineCulture: Dispatch<SetStateAction<boolean>>
  //
  betaHCG: ILaboratoryTestProperty[] | undefined
  betaHCGLoading: boolean
  betaHCGError: Error | null
  fetchBetaHCGTest: (params: IFetchLaboratoryTestsParams) => void
  urinalysis: ILaboratoryTestProperty[] | undefined
  urinalysisLoading: boolean
  urinalysisError: Error | null
  fetchUrinalysisTest: (params: IFetchLaboratoryTestsParams) => void
}

const LaboratoryHealthProceduresContext = React.createContext<ILaboratoryHealthProceduresContextProps>(
  {} as ILaboratoryHealthProceduresContextProps
)

function LaboratoryHealthProceduresProvider({ children }: LaboratoryHealthProceduresProviderProps): JSX.Element {
  const [isLoading, setIsLoading] = useState<boolean>(defaultValue.isLoading)
  const [error, setError] = useState<string>(defaultValue.error)
  const [urineCultureTests, setUrineCultureTests] = useState<IUrineCultureTest[]>(defaultValue.urineCultureTests)
  const [antibiotics, setAntibiotics] = useState<IAntibiotic[] | null>(null)

  const [selectedUrineCulture, setSelectedUrineCulture] = useState<IUrineCultureTest | null>(null)
  const [selectedOrganisms, setSelectedOrganisms] = useState<string[] | []>(defaultValue.selectedOrganisms)
  const [displayedUrineCulture, setDisplayedUrineCulture] = useState<IUrineCultureTest[]>(
    defaultValue.urineCultureTests
  )
  const [rangeOfDatesFilter, setRangeOfDatesFilter] = useState<Date[]>([])

  const [openModalCompareUrineCulture, setOpenModalCompareUrineCulture] = useState<boolean>(
    defaultValue.openModalCompareUrineCulture
  )
  const { data: selectedUrineCultureProperties, isLoading: isLoadingTestProperties } = useFetchLaboratoryTestProperties(
    { pathParams: { testId: String(selectedUrineCulture?.id) } },
    { enabled: !!selectedUrineCulture?.id }
  )

  const getHealthProcedures: IGetUrineCultureTests = useCallback(async (params: IFetchUrineCultureTestByParams) => {
    try {
      setIsLoading(true)
      const response = await fetchUrineCultureTests(params)

      if (response.length <= 0) {
        setUrineCultureTests([])
        setError(message('timeline.not_found'))
        return
      }

      setUrineCultureTests(response.reverse())
      setError('idle')
    } catch (error) {
      setUrineCultureTests([])
      setError(message('historic.historicEncounterError'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getUrineCultureDetails = useCallback(async (selectedUrineCultureTest: IUrineCultureTest) => {
    setSelectedUrineCulture(selectedUrineCultureTest)
  }, [])

  const getAntibiotics: IGetAntibiotics = useCallback(async (params: IFetchAntibioticsParams) => {
    try {
      const response = await fetchAntibiotics(params)
      setAntibiotics(response || [])
    } catch (error) {
      setAntibiotics([])
    }
  }, [])

  const filterUrineCulturePerRangeOfDates = useCallback((rangeDates: Date[]) => {
    const isAValidRangeOfDates = rangeDates.length !== 1
    if (isAValidRangeOfDates) {
      setRangeOfDatesFilter(rangeDates)
    }
  }, [])

  const filterUrineCultureByOrganism = useCallback(() => {
    if (selectedOrganisms.length === 0) {
      setDisplayedUrineCulture(urineCultureTests)
      return
    }

    const urineCultureBySelectedOrganisms = urineCultureTests.filter(urineCultureTest => {
      if (!urineCultureTest?.organism?.length) return false

      const isAValidOrganism = urineCultureTest.organism.some(organism => {
        return selectedOrganisms.includes(organism as never)
      })
      return isAValidOrganism
    })

    setDisplayedUrineCulture(urineCultureBySelectedOrganisms)
  }, [selectedOrganisms, urineCultureTests])

  const setterSelectedOrganisms = (value: string[]) => {
    setSelectedOrganisms(value)
  }

  useEffect(() => {
    const appliedFilters = urineCultureFilters(urineCultureTests).perDate(rangeOfDatesFilter).value
    setDisplayedUrineCulture(appliedFilters)
  }, [rangeOfDatesFilter, urineCultureTests])

  useEffect(() => {
    filterUrineCultureByOrganism()
  }, [selectedOrganisms, urineCultureTests])

  const [fetchBetaHCGParams, setFetchBetaHCGParams] = useState<IFetchLaboratoryTestsParams | null>(null)
  const {
    data: betaHCGTests,
    isLoading: betaHCGTestsLoading,
    error: betaHCGTestsError
  } = useFetchLaboratoryTests(fetchBetaHCGParams, {
    enabled: fetchBetaHCGParams !== null
  })
  const {
    data: betaHCG,
    isLoading: betaHCGLoading,
    error: betaHCGError
  } = useFetchLaboratoryTestProperties(
    { pathParams: { testId: String(betaHCGTests?.[0]?.id) } },
    { enabled: !!betaHCGTests?.[0]?.id }
  )

  const fetchBetaHCGTest = useCallback(
    (params: IFetchLaboratoryTestsParams) => {
      setFetchBetaHCGParams(params)
    },
    [setFetchBetaHCGParams]
  )

  const [fetchUrinalysisParams, setFetchUrinalysisParams] = useState<IFetchLaboratoryTestsParams | null>(null)
  const {
    data: urinalysisTests,
    isLoading: urinalysisTestsLoading,
    error: urinalysisTestsError
  } = useFetchLaboratoryTests(fetchUrinalysisParams, {
    enabled: fetchUrinalysisParams !== null
  })
  const {
    data: urinalysis,
    isLoading: urinalysisLoading,
    error: urinalysisError
  } = useFetchLaboratoryTestProperties(
    { pathParams: { testId: String(urinalysisTests?.[0]?.id) } },
    { enabled: !!urinalysisTests?.[0]?.id }
  )

  const fetchUrinalysisTest = useCallback(
    (params: IFetchLaboratoryTestsParams) => {
      setFetchUrinalysisParams(params)
    },
    [setFetchUrinalysisParams]
  )

  return (
    <LaboratoryHealthProceduresContext.Provider
      value={{
        urineCultureTests,
        displayedUrineCulture,
        selectedUrineCulture,
        selectedUrineCultureProperties,
        isLoadingTestProperties,
        getUrineCultureDetails,
        getHealthProcedures,
        getAntibiotics,
        antibiotics,
        filterUrineCulturePerRangeOfDates,
        filterUrineCultureByOrganism,
        selectedOrganisms,
        setterSelectedOrganisms,
        rangeOfDatesFilter,
        error,
        isLoading,
        openModalCompareUrineCulture,
        setOpenModalCompareUrineCulture,
        betaHCG,
        fetchBetaHCGTest,
        betaHCGLoading: betaHCGTestsLoading || betaHCGLoading,
        betaHCGError: betaHCGTestsError || betaHCGError,
        urinalysis,
        urinalysisLoading: urinalysisTestsLoading || urinalysisLoading,
        urinalysisError: urinalysisTestsError || urinalysisError,
        fetchUrinalysisTest
      }}
    >
      {children}
    </LaboratoryHealthProceduresContext.Provider>
  )
}

const useLaboratoryHealthProcedures = (): ILaboratoryHealthProceduresContextProps => {
  return React.useContext(LaboratoryHealthProceduresContext)
}

export { LaboratoryHealthProceduresContext, LaboratoryHealthProceduresProvider, useLaboratoryHealthProcedures }
